import {
  Accordion,
  ActionIcon,
  Box,
  ColorInput,
  Flex,
  Group,
  NumberInput,
  Stack,
  useMantineTheme,
} from '@mantine/core';
import { IconBold, IconItalic } from '@tabler/icons-react';
import type { AfternoonTableConfig, ExportBrandingConfigData, TableRegionConfig } from '../types';
import FontSelect from './FontSelect';

type Props = {
  config: ExportBrandingConfigData;
  onChange: (config: ExportBrandingConfigData) => void;
};

function RegionForm({
  value,
  onChange,
}: {
  value: TableRegionConfig;
  onChange: (v: TableRegionConfig) => void;
}) {
  return (
    <Stack gap="sm">
      <Group grow>
        <FontSelect
          value={value.font_family}
          onChange={v => onChange({ ...value, font_family: v })}
        />
        <NumberInput
          label="Size (pt)"
          value={value.font_size_pt}
          onChange={v => onChange({ ...value, font_size_pt: Number(v) || 11 })}
          min={6}
          max={36}
          step={0.5}
        />
      </Group>
      <Flex align="flex-end" gap="sm">
        <ColorInput
          label="Text colour"
          format="hex"
          value={`#${value.text_color}`}
          onChange={v => onChange({ ...value, text_color: v.replace('#', '') })}
          style={{ flex: 1 }}
        />
        <ColorInput
          label="Background colour"
          format="hex"
          value={`#${value.background_color}`}
          onChange={v => onChange({ ...value, background_color: v.replace('#', '') })}
          style={{ flex: 1 }}
        />
        <Flex align="center" gap={4} mb={2}>
          <ActionIcon
            variant={value.bold ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, bold: !value.bold })}
            size="lg"
          >
            <IconBold size={18} />
          </ActionIcon>
          <ActionIcon
            variant={value.italic ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, italic: !value.italic })}
            size="lg"
          >
            <IconItalic size={18} />
          </ActionIcon>
        </Flex>
      </Flex>
    </Stack>
  );
}

const REGIONS: {
  key: keyof AfternoonTableConfig;
  label: string;
}[] = [
  { key: 'header_row', label: 'Header row' },
  { key: 'whole_table', label: 'Whole table (defaults)' },
  { key: 'first_column', label: 'First column' },
  { key: 'total_row', label: 'Total row' },
  { key: 'odd_rows', label: 'Odd rows' },
  { key: 'even_rows', label: 'Even rows' },
];

export default function TableStyleForm({ config, onChange }: Props) {
  const table = config.afternoon_table;

  const updateRegion = (key: keyof AfternoonTableConfig, value: TableRegionConfig) => {
    onChange({
      ...config,
      afternoon_table: { ...table, [key]: value },
    });
  };

  const theme = useMantineTheme();

  return (
    <Stack gap="md">
      <Box
        p="md"
        style={{
          borderRadius: 16,
          border: `1px solid ${theme.colors.gray[2]}`,
          overflow: 'hidden',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: table.whole_table.font_family,
            fontSize: `${table.whole_table.font_size_pt}px`,
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: `#${table.header_row.background_color}`,
                color: `#${table.header_row.text_color}`,
                fontFamily: table.header_row.font_family,
                fontWeight: table.header_row.bold ? 700 : undefined,
                fontStyle: table.header_row.italic ? 'italic' : undefined,
              }}
            >
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Column A</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Column B</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Column C</th>
            </tr>
          </thead>
          <tbody>
            <tr
              style={{
                backgroundColor: `#${table.odd_rows.background_color}`,
                color: `#${table.odd_rows.text_color}`,
                fontWeight: table.odd_rows.bold ? 700 : undefined,
                fontStyle: table.odd_rows.italic ? 'italic' : undefined,
              }}
            >
              <td style={{ padding: '6px 8px' }}>Row 1</td>
              <td style={{ padding: '6px 8px' }}>Data</td>
              <td style={{ padding: '6px 8px' }}>Data</td>
            </tr>
            <tr
              style={{
                backgroundColor: `#${table.even_rows.background_color}`,
                color: `#${table.even_rows.text_color}`,
                fontWeight: table.even_rows.bold ? 700 : undefined,
                fontStyle: table.even_rows.italic ? 'italic' : undefined,
              }}
            >
              <td style={{ padding: '6px 8px' }}>Row 2</td>
              <td style={{ padding: '6px 8px' }}>Data</td>
              <td style={{ padding: '6px 8px' }}>Data</td>
            </tr>
            <tr
              style={{
                backgroundColor: `#${table.total_row.background_color}`,
                color: `#${table.total_row.text_color}`,
                fontWeight: table.total_row.bold ? 700 : undefined,
                fontStyle: table.total_row.italic ? 'italic' : undefined,
              }}
            >
              <td style={{ padding: '6px 8px' }}>Total</td>
              <td style={{ padding: '6px 8px' }}>-</td>
              <td style={{ padding: '6px 8px' }}>100</td>
            </tr>
          </tbody>
        </table>
      </Box>

      <Accordion variant="separated" multiple defaultValue={['header_row']}>
        {REGIONS.map(({ key, label }) => (
          <Accordion.Item key={key} value={key} role="region">
            <Accordion.Control>{label}</Accordion.Control>
            <Accordion.Panel>
              <RegionForm value={table[key]} onChange={v => updateRegion(key, v)} />
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}
